import React, { useEffect, useState, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDropzone } from 'react-dropzone'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  Chip,
  LinearProgress
} from '@mui/material'
import {
  Close,
  Receipt,
  Business,
  CalendarToday,
  Description,
  AttachMoney,
  CloudUpload,
  PictureAsPdf,
  Delete
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { useCreateInvoice, useUpdateInvoice } from '../../hooks/useInvoices'
import { useClients } from '../../hooks/useClients'
import api from '../../utils/api'
import { formatCurrency } from '../../utils/formatters'

// Validation schema
const invoiceSchema = yup.object({
  invoiceNumber: yup
    .string()
    .required('El número de factura es requerido'),
  clientId: yup
    .string()
    .required('Debe seleccionar un cliente'),
  issueDate: yup
    .date()
    .required('La fecha de emisión es requerida'),
  dueDate: yup
    .date()
    .required('La fecha de vencimiento es requerida')
    .min(yup.ref('issueDate'), 'La fecha de vencimiento debe ser posterior a la emisión'),
  description: yup
    .string()
    .required('La descripción es requerida')
    .min(1, 'La descripción no puede estar vacía')
    .max(1000, 'La descripción no puede exceder 1000 caracteres'),
  netAmount: yup
    .number()
    .required('El monto neto es requerido')
    .positive('El monto debe ser positivo'),
  ivaPercentage: yup
    .number()
    .min(0, 'El IVA no puede ser negativo')
    .max(100, 'El IVA no puede exceder 100%')
    .default(19)
})

const InvoiceForm = ({ 
  open, 
  onClose, 
  invoice = null, 
  title = null 
}) => {
  const isEdit = !!invoice
  const [pdfFile, setPdfFile] = useState(null)
  const [pdfPreview, setPdfPreview] = useState(null)
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractionError, setExtractionError] = useState(null)
  const [extractionSuccess, setExtractionSuccess] = useState(false)
  
  const createInvoiceMutation = useCreateInvoice()
  const updateInvoiceMutation = useUpdateInvoice()
  
  // Get clients for dropdown
  const { data: clientsData, refetch: refetchClients } = useClients({ page: 1, limit: 100 })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control
  } = useForm({
    resolver: yupResolver(invoiceSchema),
    defaultValues: {
      invoiceNumber: '',
      clientId: '',
      issueDate: dayjs(),
      dueDate: dayjs().add(30, 'day'),
      description: '',
      netAmount: '',
      ivaPercentage: 19
    }
  })

  // Watch values for calculations
  const netAmount = watch('netAmount')
  const ivaPercentage = watch('ivaPercentage')

  // Calculate amounts
  const calculatedAmounts = React.useMemo(() => {
    const net = Math.round(parseFloat(netAmount) || 0)
    const ivaPercent = parseFloat(ivaPercentage) || 0
    const ivaAmount = Math.round(net * (ivaPercent / 100))
    const totalAmount = net + ivaAmount
    
    return {
      netAmount: net,
      ivaAmount: ivaAmount,
      totalAmount: totalAmount
    }
  }, [netAmount, ivaPercentage])

  // Update form values when invoice changes
  useEffect(() => {
    if (invoice) {
      console.log('🔍 DEBUG - InvoiceForm useEffect - invoice object:', invoice)
      console.log('🔍 DEBUG - InvoiceForm useEffect - invoice.id:', invoice.id)
      console.log('🔍 DEBUG - InvoiceForm useEffect - invoice.clientId:', invoice.clientId)
      console.log('🔍 DEBUG - InvoiceForm useEffect - invoice.client:', invoice.client)
      
      setValue('invoiceNumber', invoice.invoiceNumber || '')
      // Handle both clientId and client object
      const clientId = invoice.clientId || invoice.client?.id || ''
      console.log('🔍 DEBUG - InvoiceForm useEffect - resolved clientId:', clientId)
      setValue('clientId', clientId)
      setValue('issueDate', dayjs(invoice.issueDate))
      setValue('dueDate', dayjs(invoice.dueDate))
      setValue('description', invoice.description || '')
      setValue('netAmount', parseFloat(invoice.netAmount) || '')
      setValue('ivaPercentage', parseFloat(invoice.ivaPercentage) || 19)
      
      // Set PDF preview if exists
      if (invoice.pdfSignedUrl || invoice.pdfUrl) {
        setPdfPreview(invoice.pdfSignedUrl || invoice.pdfUrl)
      }
    } else {
      reset({
        invoiceNumber: '',
        clientId: '',
        issueDate: dayjs(),
        dueDate: dayjs().add(30, 'day'),
        description: '',
        netAmount: '',
        ivaPercentage: 19
      })
      setPdfFile(null)
      setPdfPreview(null)
    }
  }, [invoice, setValue, reset])

  // Auto-calculate due date when client or issue date changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if ((name === 'clientId' || name === 'issueDate') && value.clientId && value.issueDate) {
        const newDueDate = calculateDueDate(value.issueDate, value.clientId)
        if (newDueDate && !isEdit) { // Only auto-calculate for new invoices
          setValue('dueDate', newDueDate)
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue, isEdit, clientsData])

  const isLoading = createInvoiceMutation.isLoading || updateInvoiceMutation.isLoading

  const onSubmit = async (data) => {
    try {
      console.log('🔍 DEBUG - onSubmit - isEdit:', isEdit)
      console.log('🔍 DEBUG - onSubmit - invoice.id:', invoice?.id)
      console.log('🔍 DEBUG - onSubmit - form data:', data)
      
      const formData = new FormData()
      
      // Add form fields
      formData.append('invoiceNumber', data.invoiceNumber)
      formData.append('clientId', data.clientId)
      formData.append('issueDate', data.issueDate.toISOString())
      formData.append('dueDate', data.dueDate.toISOString())
      formData.append('description', data.description)
      formData.append('netAmount', data.netAmount)
      formData.append('ivaPercentage', data.ivaPercentage)
      
      // Add PDF file if selected
      if (pdfFile) {
        formData.append('pdf', pdfFile)
      }

      console.log('🔍 DEBUG - onSubmit - FormData contents:')
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value)
      }

      if (isEdit) {
        console.log('🔍 DEBUG - onSubmit - Calling updateInvoiceMutation')
        await updateInvoiceMutation.mutateAsync({
          id: invoice.id,
          formData
        })
        console.log('🔍 DEBUG - onSubmit - updateInvoiceMutation completed')
      } else {
        console.log('🔍 DEBUG - onSubmit - Calling createInvoiceMutation')
        await createInvoiceMutation.mutateAsync(formData)
        console.log('🔍 DEBUG - onSubmit - createInvoiceMutation completed')
      }
      
      console.log('🔍 DEBUG - onSubmit - Calling handleClose')
      handleClose()
    } catch (error) {
      console.error('❌ Error saving invoice:', error)
      console.error('❌ Error details:', error.response?.data || error.message)
    }
  }

  const handleClose = () => {
    console.log('🔍 DEBUG - handleClose - Called')
    reset()
    setPdfFile(null)
    setPdfPreview(null)
    setExtractionError(null)
    setExtractionSuccess(false)
    console.log('🔍 DEBUG - handleClose - Calling onClose')
    onClose()
    console.log('🔍 DEBUG - handleClose - onClose completed')
  }

  // PDF Upload handling
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (file) {
      setPdfFile(file)
      setPdfPreview(URL.createObjectURL(file))
      
      // Auto-extract data from PDF
      await extractDataFromPDF(file)
    }
  }, [])

  const extractDataFromPDF = async (file) => {
    try {
      setIsExtracting(true)
      setExtractionError(null)

      const formData = new FormData()
      formData.append('pdf', file)

      const response = await api.post('/invoices/extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const { extractedData } = response.data
      
      console.log('Extracted data:', extractedData)
      console.log('Original file name:', file.name)
      console.log('Returned file name:', extractedData.originalFileName)

      // Fill form with extracted data
      if (extractedData.invoiceData) {
        const { invoiceData, clientData, clientExists, existingClientId } = extractedData

        // Set invoice data
        setValue('invoiceNumber', invoiceData.invoiceNumber || '')
        
        // Parse and validate date
        const issueDate = invoiceData.issueDate 
          ? (dayjs(invoiceData.issueDate).isValid() 
              ? dayjs(invoiceData.issueDate) 
              : dayjs())
          : dayjs()
        setValue('issueDate', issueDate)
        
        setValue('description', invoiceData.description || '')
        setValue('netAmount', invoiceData.netAmount && !isNaN(invoiceData.netAmount) ? invoiceData.netAmount : '')
        setValue('ivaPercentage', invoiceData.ivaPercentage && !isNaN(invoiceData.ivaPercentage) ? invoiceData.ivaPercentage : 19)

        // Handle client and calculate due date
        let selectedClient = null
        if (clientExists && existingClientId) {
          setValue('clientId', existingClientId)
          selectedClient = clientsData?.clients?.find(c => c.id === existingClientId)
        } else {
          // Double check in local data
          const localClient = findClientByRut(clientData.rut)
          if (localClient) {
            setValue('clientId', localClient.id)
            selectedClient = localClient
          } else {
            // Create new client automatically
            const newClientId = await createNewClient(clientData)
            if (newClientId) {
              setValue('clientId', newClientId)
              // Use default payment terms for new client
              selectedClient = { paymentTermsDays: 30 }
            }
          }
        }

        // Calculate and set due date based on client's payment terms
        if (selectedClient && invoiceData.issueDate) {
          const issueDate = dayjs(invoiceData.issueDate)
          const dueDate = issueDate.add(selectedClient.paymentTermsDays || 30, 'day')
          setValue('dueDate', dueDate)
        }
        
        // Show success message
        setExtractionSuccess(true)
        setTimeout(() => setExtractionSuccess(false), 3000)
      }

    } catch (error) {
      console.error('Error extracting PDF data:', error)
      setExtractionError(error.response?.data?.message || 'Error extrayendo datos del PDF')
    } finally {
      setIsExtracting(false)
    }
  }

  const findClientByRut = (rut) => {
    if (!rut || !clientsData?.clients) return null
    
    // Clean RUT by removing dots, dashes, and verification digit
    const cleanRut = (rutString) => {
      if (!rutString) return ''
      return rutString.replace(/[.-]/g, '').replace(/\d$/, '')
    }
    
    const cleanedRut = cleanRut(rut)
    return clientsData.clients.find(client => 
      client.rut && cleanRut(client.rut) === cleanedRut
    )
  }

  const calculateDueDate = (issueDate, clientId) => {
    if (!issueDate || !clientId || !clientsData?.clients) return null
    
    const client = clientsData.clients.find(c => c.id === clientId)
    if (!client) return null
    
    const issueDayjs = dayjs(issueDate)
    return issueDayjs.add(client.paymentTermsDays || 30, 'day')
  }

  const createNewClient = async (clientData) => {
    try {
      // Mapear clientData a la estructura esperada por el backend
      const clientPayload = {
        name: clientData.name,
        rut: clientData.cleanRut || clientData.rut, // Usar cleanRut si está disponible
        email: clientData.email,
        phone: clientData.phone,
        address: clientData.address,
        paymentTermsDays: clientData.paymentTermsDays || 30
      }
      
      console.log('Creating client with payload:', clientPayload)
      
      const response = await api.post('/clients', clientPayload)
      const newClient = response.data.data // El backend devuelve { data: client }
      
      console.log('Client created successfully:', newClient)
      
      // Refresh clients data to include the new client
      await refetchClients()
      
      // Small delay to ensure data is updated
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Return the new client ID
      return newClient.id
      
    } catch (error) {
      console.error('Error creating client:', error)
      console.error('Error response:', error.response?.data)
      setExtractionError('Error creando cliente automáticamente: ' + (error.response?.data?.message || error.message))
      return null
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  const handleRemovePdf = () => {
    setPdfFile(null)
    setPdfPreview(null)
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt color="primary" />
            <Typography variant="h6">
              {title || (isEdit ? 'Editar Factura' : 'Nueva Factura')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Left Column - Form Fields */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                {/* Invoice Number */}
                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('invoiceNumber')}
                    fullWidth
                    label="Número de Factura"
                    placeholder="F-001-2024"
                    error={!!errors.invoiceNumber}
                    helperText={errors.invoiceNumber?.message}
                    disabled={isLoading}
                  />
                </Grid>

                {/* Client */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.clientId}>
                    <InputLabel>Cliente</InputLabel>
                    <Controller
                      name="clientId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Cliente"
                          disabled={isLoading}
                        >
                          {clientsData?.clients?.map((client) => (
                            <MenuItem key={client.id} value={client.id}>
                              {client.name} ({client.rut})
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.clientId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.clientId.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Issue Date */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="issueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Fecha de Emisión"
                        disabled={isLoading}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.issueDate,
                            helperText: errors.issueDate?.message
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Due Date */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Fecha de Vencimiento"
                        disabled={isLoading}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.dueDate,
                            helperText: errors.dueDate?.message
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    {...register('description')}
                    fullWidth
                    label="Descripción"
                    multiline
                    rows={3}
                    placeholder="Describe los productos o servicios facturados..."
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    disabled={isLoading}
                  />
                </Grid>

                {/* Net Amount */}
                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('netAmount')}
                    fullWidth
                    label="Monto Neto"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    error={!!errors.netAmount}
                    helperText={errors.netAmount?.message}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
                </Grid>

                {/* IVA Percentage */}
                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('ivaPercentage')}
                    fullWidth
                    label="IVA (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    error={!!errors.ivaPercentage}
                    helperText={errors.ivaPercentage?.message}
                    disabled={isLoading}
                  />
                </Grid>

                {/* Calculated Amounts */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cálculo de Montos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Neto:
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(calculatedAmounts.netAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          IVA ({ivaPercentage}%):
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatCurrency(calculatedAmounts.ivaAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total:
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary.main">
                          {formatCurrency(calculatedAmounts.totalAmount)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column - PDF Upload */}
            <Grid item xs={12} md={5}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Archivo PDF
                </Typography>
                
                {/* PDF Upload Area */}
                <Paper
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.default',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    mb: 2,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <input {...getInputProps()} />
                  {isExtracting ? (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" gutterBottom color="primary.main">
                        Extrayendo datos con IA...
                      </Typography>
                      <LinearProgress sx={{ width: '80%', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Analizando PDF con OpenAI
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Suelta el archivo aquí' : 'Arrastra o haz clic'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Archivo PDF (máx. 10MB)
                      </Typography>
                      <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                        ✨ Extracción automática con IA
                      </Typography>
                    </>
                  )}
                </Paper>

                {/* PDF Preview */}
                {(pdfFile || pdfPreview) && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PictureAsPdf color="error" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {pdfFile ? pdfFile.name : 'PDF existente'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pdfFile ? `${(pdfFile.size / 1024 / 1024).toFixed(2)} MB` : 'Archivo actual'}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton size="small" onClick={handleRemovePdf}>
                        <Delete />
                      </IconButton>
                    </Box>
                  </Paper>
                )}

                {/* Extraction Success */}
                {extractionSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      ✨ Datos extraídos automáticamente con IA
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Revisa los campos y ajusta si es necesario.
                    </Typography>
                  </Alert>
                )}

                {/* Extraction Error */}
                {extractionError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {extractionError}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Puedes completar los campos manualmente.
                    </Typography>
                  </Alert>
                )}

                {/* Upload Progress */}
                {isLoading && (pdfFile || isEdit) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {isEdit ? 'Actualizando factura...' : 'Subiendo archivo...'}
                    </Typography>
                    <LinearProgress />
                  </Box>
                )}

                {/* PDF Info */}
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Información:</strong><br />
                    • Solo archivos PDF<br />
                    • Tamaño máximo: 10MB<br />
                    • Se almacena en Amazon S3<br />
                    • URLs firmadas para seguridad
                  </Typography>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            disabled={isLoading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{ minWidth: 140 }}
          >
            {isLoading 
              ? (isEdit ? 'Actualizando...' : 'Creando...') 
              : (isEdit ? 'Actualizar Factura' : 'Crear Factura')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default InvoiceForm
